import { Breadcrumbs, constants, FileUpload, Flex, form, H2, P, usePageTitle } from '@inlight-media/linfox-ui-library';
import { grid } from 'styled-system';
import { observer } from 'mobx-react';
import { useCallback, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useRootStore } from '@inlight-media/linfox-state-management';
import styled from 'styled-components';

import { AdditionalReferences } from '../../components/additional-references';
import { CaseFormSection } from '../../components/case-form-section';
import { DynamicFormInput } from '../../components/dynamic-form-input';
import { HeaderBar } from '../../components/header-bar';
import { LabelledInput } from '../../components/labelled-input';
import { ModalContextProvider } from '../../components/modal-context';
import { SKUListing } from '../../components/sku-listing';
import { TemplateContextProvider } from '../../components/template-context';
import { useFetchTemplate } from '../../hooks/use-fetch-template';

const { Form, SubmitButton } = form;

const CaseCreate = () => {
  usePageTitle('Create Case');
  const history = useHistory();
  const { CaseStore } = useRootStore();

  const formSections = useRef({});

  const [modalContainer, setModalContainer] = useState();

  const { contractID } = useParams();

  const onSubmit = async () => {
    const allForms = Object.values(formSections.current);

    const values = allForms.reduce((acc, cur) => {
      return { ...acc, ...cur.getValues() };
    }, {});

    const { success } = await CaseStore.createCase({ contractID, caseDetails: values });

    if (success) {
      history.push('/cases');
    }
  };

  const breadCrumbTrail = {
    title: 'Create Case',
    ancestors: [
      {
        title: 'All Cases',
        url: '/cases',
      },
    ],
  };

  const templateData = useFetchTemplate(contractID);

  // TODO: determine programmatically
  const skuFields = true;

  const mount = useCallback(
    (id) => (node) => {
      formSections.current[id] = node;
    },
    [formSections]
  );

  if (!templateData) return null;

  const { contractName } = templateData;

  return (
    <>
      <div ref={setModalContainer} />
      <HeaderBar>
        <Flex
          flex={1}
          maxWidth={constants.globalStyles.pageMaxWidth}
          justifyContent="space-between"
          alignItems="center"
        >
          <Breadcrumbs entry={breadCrumbTrail} margin="0" />
        </Flex>
      </HeaderBar>
      <BackgroundWrapper>
        <ModalContextProvider value={{ modalContainer }}>
          <ContentWrapper>
            <TemplateContextProvider value={{ templateData }}>
              <>
                <div>
                  <H2>Create Case</H2>
                  <P color="greyFive">{contractName}</P>
                </div>
                <Form ref={mount('overview')}>
                  <CaseFormSection title="Overview">
                    <DynamicFormInput
                      name="caseTitle"
                      render={(props) => (
                        <Cell gridColumn="span 3">
                          <LabelledInput {...props} />
                        </Cell>
                      )}
                    />
                    <DynamicFormInput
                      name="caseType"
                      render={(props) => (
                        <Cell gridColumn="span 2">
                          <LabelledInput {...props} />
                        </Cell>
                      )}
                    />
                    <DynamicFormInput
                      name="casePriority"
                      render={(props) => (
                        <Cell gridColumn="span 1">
                          <LabelledInput {...props} />
                        </Cell>
                      )}
                    />
                    <DynamicFormInput
                      name="description"
                      render={(props) => (
                        <Cell gridColumn="span 6">
                          <LabelledInput {...props} />
                        </Cell>
                      )}
                    />
                    <AdditionalReferences
                      render={({ id, ...props }) => (
                        <Cell gridColumn="span 3">
                          <LabelledInput {...props} />
                        </Cell>
                      )}
                    />
                  </CaseFormSection>
                </Form>
                <Form ref={mount('details')}>
                  <CaseFormSection title="Details">
                    <DynamicFormInput
                      name="orderNumber"
                      render={(props) => (
                        <Cell gridColumn="span 3">
                          <LabelledInput {...props} />
                        </Cell>
                      )}
                    />
                    <DynamicFormInput
                      name="loadNumber"
                      render={(props) => (
                        <Cell gridColumn="span 3">
                          <LabelledInput {...props} />
                        </Cell>
                      )}
                    />
                    <DynamicFormInput
                      name="freightClass"
                      render={(props) => (
                        <Cell gridColumn="span 3">
                          <LabelledInput {...props} />
                        </Cell>
                      )}
                    />
                    <DynamicFormInput
                      name="customerReferenceNumber"
                      render={(props) => (
                        <Cell gridColumn="span 3">
                          <LabelledInput {...props} />
                        </Cell>
                      )}
                    />
                    <DynamicFormInput
                      name="purchaseOrderNumber"
                      render={(props) => (
                        <Cell gridColumn="span 3">
                          <LabelledInput {...props} />
                        </Cell>
                      )}
                    />
                    <DynamicFormInput
                      name="deliveryDate"
                      render={(props) => (
                        <Cell gridColumn="span 3">
                          <LabelledInput {...props} />
                        </Cell>
                      )}
                    />
                    <DynamicFormInput
                      name="caseDeliveryState"
                      render={(props) => (
                        <Cell gridColumn="span 3">
                          <LabelledInput {...props} />
                        </Cell>
                      )}
                    />
                    <DynamicFormInput
                      name="site"
                      render={(props) => (
                        <Cell gridColumn="span 3">
                          <LabelledInput {...props} />
                        </Cell>
                      )}
                    />
                    <DynamicFormInput
                      name="entireOrder"
                      render={(props) => (
                        <Cell gridColumn="span 3">
                          <LabelledInput {...props} />
                        </Cell>
                      )}
                    />
                  </CaseFormSection>
                </Form>
                {skuFields && (
                  <Form ref={mount('sku')}>
                    <CaseFormSection title="SKU">
                      <SKUListing />
                    </CaseFormSection>
                  </Form>
                )}
              </>
              <Form ref={mount('attachments')}>
                <CaseFormSection title="Attachments">
                  <Wrapper>
                    <FileUpload name="attachments" canUploadMultiple />
                  </Wrapper>
                </CaseFormSection>
              </Form>
            </TemplateContextProvider>
            <ButtonWrapper>
              <Form onSubmit={onSubmit}>
                <SubmitButton gridColumn="-1" size="medium">
                  Create Case
                </SubmitButton>
              </Form>
            </ButtonWrapper>
          </ContentWrapper>
        </ModalContextProvider>
      </BackgroundWrapper>
    </>
  );
};

// TODO: this should be handled elsewhere, but that would involve deeper refactoring
const BackgroundWrapper = styled.div`
  background-color: ${constants.globalStyles.backgroundColor};
`;

const ButtonWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr max-content;
`;

const Cell = styled.div`
  align-self: center;
  ${grid};
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-gap: 3rem;
  margin: 0 auto;
  padding: 3rem 0;
  width: ${constants.globalStyles.detailsMaxWidth};
`;

const Wrapper = styled.div`
  display: grid;
  grid-column: 1 / -1;
`;

const ObservedCaseCreate = observer(CaseCreate);
export { ObservedCaseCreate as CaseCreate };
